import type { AttendanceItem } from "views/Attendance/types"
import type { RequestLeaveItem } from "views/RequestLeave/types"

import { Brightness1Sharp, WatchLater, WatchLaterOutlined } from "@mui/icons-material"
import { Box, Chip, Stack, Typography } from "@mui/material"
import { DetailDrawer, Flex, FlexColumn } from "components/common"
import { DateTime } from "luxon"
import { ATTENDANCE_ACTION_LABELS } from "views/Attendance/constants"
import {
  calculateWorkingTime,
  getAttendanceActionTime,
  getFirstCheckIn,
  getLastCheckOut,
  isCheckInAction,
  isCheckinLate,
  sortAttendancesByActionTime,
} from "views/Attendance/utils"
import { REQUEST_ACTION_LABELS, REQUEST_STATUS_COLORS, REQUEST_STATUS_LABELS } from "views/RequestLeave/constants"

type Props = {
  attendances: AttendanceItem[]
  attendeeName?: null | string
  date: DateTime | null
  leaves: RequestLeaveItem[]
  onClose: () => void
}

const formatTime = (time: DateTime | null) => (time ? time.toFormat("HH:mm") : "—")

const AttendanceDetailDrawer = ({ attendances, attendeeName, date, leaves, onClose }: Props) => {
  const sortedAttendances = sortAttendancesByActionTime(attendances)
  const workingMinutes = calculateWorkingTime(sortedAttendances)
  const hasData = sortedAttendances.length > 0 || leaves.length > 0

  return (
    <DetailDrawer
      onClose={onClose}
      open={Boolean(date)}
      subtitle={attendeeName || "Chi tiết chấm công"}
      title={`Ngày ${date?.toFormat("dd/MM/yyyy") ?? ""}`}
    >
      {!hasData ? (
        <Typography color="text.secondary" textAlign="center" variant="body2">
          Không có dữ liệu chấm công
        </Typography>
      ) : (
        <FlexColumn gap={3}>
          <Flex className="flex-wrap" gap={1}>
            <Chip
              color="primary"
              icon={<WatchLaterOutlined />}
              label={`${(workingMinutes / 60).toFixed(2)} giờ`}
              size="small"
              variant="outlined"
            />
            <Chip
              color={isCheckinLate(sortedAttendances) ? "warning" : "default"}
              icon={<WatchLater />}
              label={isCheckinLate(sortedAttendances) ? "Đi muộn" : "Đúng giờ"}
              size="small"
              variant="outlined"
            />
            <Chip
              color="success"
              label={`Check-in: ${formatTime(getFirstCheckIn(sortedAttendances))}`}
              size="small"
              variant="outlined"
            />
            <Chip
              color="error"
              label={`Check-out: ${formatTime(getLastCheckOut(sortedAttendances))}`}
              size="small"
              variant="outlined"
            />
          </Flex>

          <FlexColumn gap={1}>
            <Typography fontWeight={700} variant="subtitle2">
              Lượt chấm công
            </Typography>
            {sortedAttendances.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                Không có lượt chấm công
              </Typography>
            ) : (
              <Stack gap={1}>
                {sortedAttendances.map((attendance) => {
                  const isCheckIn = isCheckInAction(attendance.action)
                  return (
                    <Box border={1} borderColor="divider" borderRadius={1} key={attendance.id} px={1.5} py={1}>
                      <Flex alignItems="center" gap={1} justifyContent="space-between">
                        <Flex alignItems="center" gap={1}>
                          <Brightness1Sharp
                            color={isCheckIn ? "success" : "error"}
                            sx={(theme) => ({ fontSize: theme.spacing(1.25) })}
                          />
                          <Typography variant="body2">{ATTENDANCE_ACTION_LABELS[attendance.action]}</Typography>
                        </Flex>
                        <Typography color="text.secondary" variant="body2">
                          {getAttendanceActionTime(attendance).toFormat("HH:mm")}
                        </Typography>
                      </Flex>
                    </Box>
                  )
                })}
              </Stack>
            )}
          </FlexColumn>

          <FlexColumn gap={1}>
            <Typography fontWeight={700} variant="subtitle2">
              Yêu cầu liên quan
            </Typography>
            {leaves.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                Không có yêu cầu liên quan
              </Typography>
            ) : (
              <Stack gap={1}>
                {leaves.map((leave) => (
                  <Box border={1} borderColor="divider" borderRadius={1} key={leave.id} px={1.5} py={1}>
                    <Flex alignItems="center" gap={1} justifyContent="space-between">
                      <Typography fontWeight={700} variant="body2">
                        {REQUEST_ACTION_LABELS[leave.action]}
                      </Typography>
                      <Chip
                        color={REQUEST_STATUS_COLORS[leave.status]}
                        label={REQUEST_STATUS_LABELS[leave.status]}
                        size="small"
                        variant="outlined"
                      />
                    </Flex>
                    <Typography color="text.secondary" variant="body2">
                      {leave.reason}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </FlexColumn>
        </FlexColumn>
      )}
    </DetailDrawer>
  )
}

export default AttendanceDetailDrawer
